// Admin → per-supplier Catalogue viewer.
//
// Read-only browser of one supplier's product catalogue. Suppliers manage
// their own catalogue from the supplier portal; admin just inspects.
//
// Entry: clicked from the Suppliers list (handleRowAction "catalogue"
// → setCurrentSupplier(...) → ctx.navigate("catalogue")).
//
// Data: /api/catalogue/{categories,search,imports}?type=supplier&id=<slug>
// — the same PG-backed endpoints the supplier portal uses (see
// kitchenos-api/modules/catalogue.js after the 2026-05-24 PG migration).
//
// Hidden from the sidebar — admin discovers it via the per-row button.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";
import { getCurrentSupplier } from "./state";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface ProductRow {
  sku: string;
  desc: string;
  price: number;
  h: number;
  w: number;
  d: number;
  range: string;
  colour: string;
  category: string;
}

interface ImportRow {
  id: string;
  filename: string;
  row_count: number;
  success_count: number;
  error_count: number;
  imported_at: string;
}

// ── Formatting helpers ───────────────────────────────────────────────────────

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 2,
});

const NUM = new Intl.NumberFormat("en-GB");

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function formatDimensions(p: ProductRow): string {
  const parts: string[] = [];
  if (p.h) parts.push(`H${p.h}`);
  if (p.w) parts.push(`W${p.w}`);
  if (p.d) parts.push(`D${p.d}`);
  return parts.length ? parts.join(" × ") : "—";
}

// ── Data fetch ───────────────────────────────────────────────────────────────

interface CategoryRow {
  name: string;
  productCount: number;
}

async function loadCategories(
  ctx: PageContext,
  supplierId: string,
  signal: AbortSignal,
): Promise<CategoryRow[]> {
  try {
    const resp = await ctx.api<{ categories?: CategoryRow[] }>(
      `/catalogue/categories?type=supplier&id=${encodeURIComponent(supplierId)}`,
      { signal },
    );
    return Array.isArray(resp.categories)
      ? resp.categories.map((c) => ({
          name: String(c?.name ?? ""),
          productCount: typeof c?.productCount === "number" ? c.productCount : 0,
        }))
      : [];
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) return [];
    throw err;
  }
}

async function loadProducts(
  ctx: PageContext,
  supplierId: string,
  q: string,
  signal: AbortSignal,
): Promise<ProductRow[]> {
  const qs = new URLSearchParams({ type: "supplier", id: supplierId });
  if (q) qs.set("q", q);
  qs.set("limit", "500");
  try {
    const resp = await ctx.api<{ results?: ProductRow[] }>(
      `/catalogue/search?${qs.toString()}`,
      { signal },
    );
    return resp.results ?? [];
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) return [];
    throw err;
  }
}

async function loadImports(
  ctx: PageContext,
  supplierId: string,
  signal: AbortSignal,
): Promise<ImportRow[]> {
  try {
    const resp = await ctx.api<{ imports?: ImportRow[] }>(
      `/catalogue/imports?type=supplier&id=${encodeURIComponent(supplierId)}`,
      { signal },
    );
    return Array.isArray(resp.imports) ? resp.imports : [];
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) return [];
    throw err;
  }
}

// ── Render ───────────────────────────────────────────────────────────────────

function renderEmptyState(container: HTMLElement, ctx: PageContext): void {
  container.innerHTML = `
    <section class="acat-empty-state">
      <h2>No supplier selected</h2>
      <p>Open this page from the <b>Suppliers</b> list by clicking a supplier's Catalogue button.</p>
      <button type="button" class="adm-btn" data-action="go-suppliers">Go to Suppliers</button>
    </section>
  `;
  const goBtn = container.querySelector<HTMLButtonElement>("[data-action='go-suppliers']");
  goBtn?.addEventListener("click", () => ctx.navigate("suppliers"));
}

function renderShell(container: HTMLElement, supplierId: string, supplierName: string): void {
  container.innerHTML = `
    <header class="acat-header">
      <div class="acat-header__left">
        <button class="acat-back" type="button" data-action="back" aria-label="Back to Suppliers">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="1.5"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <div class="acat-titles">
          <h1>${escapeHtml(supplierName)} — Catalogue</h1>
          <p>${escapeHtml(supplierId)}</p>
        </div>
      </div>
    </header>

    <section class="acat-stats" data-role="stats">
      <div class="acat-stat">
        <div class="acat-stat__lbl">Products</div>
        <div class="acat-stat__val" data-role="stat-products">—</div>
      </div>
      <div class="acat-stat">
        <div class="acat-stat__lbl">Categories</div>
        <div class="acat-stat__val" data-role="stat-categories">—</div>
      </div>
      <div class="acat-stat">
        <div class="acat-stat__lbl">Last import</div>
        <div class="acat-stat__val" data-role="stat-last-import">—</div>
        <div class="acat-stat__sub" data-role="stat-last-import-sub"></div>
      </div>
    </section>

    <div class="acat-body">
      <aside class="acat-side">
        <div class="acat-side__title">Categories</div>
        <ul class="acat-cat-list" data-role="categories"></ul>
      </aside>

      <div class="acat-main">
        <div class="acat-toolbar">
          <input type="search" class="adm-input acat-search" data-role="search"
                 placeholder="Search products by code, name, or range…" />
          <div class="acat-results-meta" data-role="results-meta"></div>
        </div>

        <div class="acat-card">
          <header class="acat-card__head">
            <div class="acat-card__title">Products</div>
          </header>
          <div class="acat-products-scroll">
            <table class="acat-products-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Range / Colour</th>
                  <th>Dimensions (mm)</th>
                  <th class="acat-num">Price</th>
                </tr>
              </thead>
              <tbody data-role="products"></tbody>
            </table>
          </div>
        </div>

        <div class="acat-card">
          <header class="acat-card__head">
            <div class="acat-card__title">Recent CSV imports</div>
          </header>
          <ul class="acat-imports" data-role="imports"></ul>
        </div>
      </div>
    </div>
  `;
}

function renderCategories(
  container: HTMLElement,
  categories: string[],
  counts: Map<string, number>,
  total: number,
  selected: string | null,
): void {
  const ul = container.querySelector<HTMLElement>("[data-role='categories']");
  if (!ul) return;
  if (categories.length === 0) {
    ul.innerHTML = `<li class="acat-cat-empty">No categories yet.</li>`;
    return;
  }
  const allActive = selected === null ? "acat-cat-item--active" : "";
  const itemsHtml = [
    `<li class="acat-cat-item ${allActive}" data-cat="">
       <span>All</span>
       <span class="acat-cat-count">${NUM.format(total)}</span>
     </li>`,
    ...categories.map((c) => {
      const isActive = c === selected ? "acat-cat-item--active" : "";
      const count = counts.get(c) ?? 0;
      return `<li class="acat-cat-item ${isActive}" data-cat="${escapeHtml(c)}">
                <span>${escapeHtml(c)}</span>
                <span class="acat-cat-count">${NUM.format(count)}</span>
              </li>`;
    }),
  ];
  ul.innerHTML = itemsHtml.join("");
}

function renderProducts(
  container: HTMLElement,
  products: ProductRow[],
): void {
  const tbody = container.querySelector<HTMLElement>("[data-role='products']");
  if (!tbody) return;
  if (products.length === 0) {
    tbody.innerHTML = `<tr class="acat-empty-row"><td colspan="6">No products to show.</td></tr>`;
    return;
  }
  tbody.innerHTML = products
    .map((p) => {
      const rangeColour = [p.range, p.colour].filter(Boolean).map(escapeHtml).join(" · ");
      return `<tr>
        <td><code>${escapeHtml(p.sku)}</code></td>
        <td class="acat-desc" title="${escapeHtml(p.desc)}">${escapeHtml(p.desc)}</td>
        <td>${escapeHtml(p.category || "—")}</td>
        <td>${rangeColour || "—"}</td>
        <td>${escapeHtml(formatDimensions(p))}</td>
        <td class="acat-num">${escapeHtml(GBP.format(p.price))}</td>
      </tr>`;
    })
    .join("");
}

function renderImports(container: HTMLElement, imports: ImportRow[]): void {
  const ul = container.querySelector<HTMLElement>("[data-role='imports']");
  if (!ul) return;
  if (imports.length === 0) {
    ul.innerHTML = `<li class="acat-imports-empty">No CSV imports recorded yet.</li>`;
    return;
  }
  ul.innerHTML = imports
    .map((i) => {
      const errorPill =
        i.error_count > 0
          ? `<span class="adm-pill adm-pill-warn">${NUM.format(i.error_count)} errors</span>`
          : `<span class="adm-pill adm-pill-ok">clean</span>`;
      return `<li>
        <span class="acat-imports__file">${escapeHtml(i.filename || "(unnamed)")}</span>
        <span class="acat-imports__meta">${NUM.format(i.success_count)} / ${NUM.format(i.row_count)} rows</span>
        ${errorPill}
        <span class="acat-imports__date">${escapeHtml(formatDate(i.imported_at))}</span>
      </li>`;
    })
    .join("");
}

function renderStats(
  container: HTMLElement,
  productCount: number,
  categoryCount: number,
  lastImport: ImportRow | null,
): void {
  const setText = (sel: string, text: string): void => {
    const el = container.querySelector<HTMLElement>(`[data-role='${sel}']`);
    if (el) el.textContent = text;
  };
  setText("stat-products", NUM.format(productCount));
  setText("stat-categories", NUM.format(categoryCount));
  if (lastImport) {
    setText("stat-last-import", formatDate(lastImport.imported_at));
    setText(
      "stat-last-import-sub",
      `${NUM.format(lastImport.success_count)} rows · ${escapeHtml(lastImport.filename || "")}`,
    );
  } else {
    setText("stat-last-import", "—");
    setText("stat-last-import-sub", "");
  }
}

// ── Page ─────────────────────────────────────────────────────────────────────

const page: Page = {
  name: "catalogue",
  label: "Catalogue",
  hidden: true,
  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ref = getCurrentSupplier();
    if (!ref) {
      renderEmptyState(container, ctx);
      return () => { /* no-op */ };
    }
    const supplierId: string = ref.id;
    const supplierName: string = ref.name;

    renderShell(container, supplierId, supplierName);

    const ac = new AbortController();
    let allProducts: ProductRow[] = [];
    let categories: string[] = [];
    // Server-side product counts per category. Sourced from
    // /catalogue/categories so we don't undercount when /search caps results.
    let categoryCounts: Map<string, number> = new Map<string, number>();
    let imports: ImportRow[] = [];
    let selectedCategory: string | null = null;
    let searchQuery = "";

    function applyFilters(): void {
      const lcQuery = searchQuery.trim().toLowerCase();
      let filtered = allProducts;
      if (selectedCategory) {
        filtered = filtered.filter((p) => p.category === selectedCategory);
      }
      if (lcQuery) {
        filtered = filtered.filter((p) =>
          p.sku.toLowerCase().includes(lcQuery)
          || p.desc.toLowerCase().includes(lcQuery)
          || (p.range || "").toLowerCase().includes(lcQuery),
        );
      }
      renderProducts(container, filtered);

      const meta = container.querySelector<HTMLElement>("[data-role='results-meta']");
      if (meta) {
        if (filtered.length === allProducts.length) {
          meta.textContent = `${NUM.format(filtered.length)} products`;
        } else {
          meta.textContent = `${NUM.format(filtered.length)} of ${NUM.format(allProducts.length)} products`;
        }
      }
    }

    function rerenderCategories(): void {
      // Counts come from the server-side GROUP BY exposed by
      // /catalogue/categories — filtering allProducts client-side would
      // undercount because loadProducts caps the response at 500 rows.
      const total = Array.from(categoryCounts.values())
        .reduce((sum, n) => sum + n, 0);
      renderCategories(container, categories, categoryCounts, total, selectedCategory);
    }

    function lastImport(): ImportRow | null {
      return imports[0] ?? null;
    }

    async function load(): Promise<void> {
      try {
        const [cats, prods, imps] = await Promise.all([
          loadCategories(ctx, supplierId, ac.signal),
          loadProducts(ctx, supplierId, "", ac.signal),
          loadImports(ctx, supplierId, ac.signal),
        ]);
        if (ac.signal.aborted) return;
        categories = cats.map((c) => c.name);
        categoryCounts = new Map(cats.map((c) => [c.name, c.productCount]));
        allProducts = prods;
        imports = imps;

        // Total products = sum of server-side category counts (covers
        // categories with >500 rows that allProducts would truncate). The
        // /search projection feeds the products table below, where 500 is
        // a perfectly fine ceiling for browsing.
        const totalProducts = Array.from(categoryCounts.values())
          .reduce((sum, n) => sum + n, 0);
        renderStats(container, totalProducts, categories.length, lastImport());
        rerenderCategories();
        applyFilters();
        renderImports(container, imports);
      } catch (err) {
        if (ac.signal.aborted) return;
        const msg = err instanceof ApiError
          ? `Couldn't load catalogue (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "Couldn't load catalogue";
        ctx.showToast(msg, "error");
      }
    }

    // ── Event wiring ─────────────────────────────────────────────────────
    const backBtn = container.querySelector<HTMLButtonElement>("[data-action='back']");
    const onBack = (): void => ctx.navigate("suppliers");
    backBtn?.addEventListener("click", onBack);

    const ul = container.querySelector<HTMLElement>("[data-role='categories']");
    const onCatClick = (e: Event): void => {
      const target = (e.target as HTMLElement).closest<HTMLElement>("[data-cat]");
      if (!target) return;
      const cat = target.getAttribute("data-cat") || "";
      selectedCategory = cat === "" ? null : cat;
      rerenderCategories();
      applyFilters();
    };
    ul?.addEventListener("click", onCatClick);

    const searchInput = container.querySelector<HTMLInputElement>("[data-role='search']");
    let searchTimer: number | undefined;
    const onSearch = (e: Event): void => {
      searchQuery = (e.target as HTMLInputElement).value;
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => applyFilters(), 120);
    };
    searchInput?.addEventListener("input", onSearch);

    void load();

    return () => {
      ac.abort();
      window.clearTimeout(searchTimer);
      backBtn?.removeEventListener("click", onBack);
      ul?.removeEventListener("click", onCatClick);
      searchInput?.removeEventListener("input", onSearch);
    };
  },
};

registerPage(page);
